import { useState } from "react";
import { useI18n } from "@/i18n";
import { Reveal, SectionHead } from "@/components/motion";

const CURRENCIES = ["EUR", "XOF", "XAF", "USD"];
const DEFAULTS = { EUR: 0.3, XOF: 150, XAF: 150, USD: 0.35 };

const Num = ({ label, value, onChange, id, step = "any" }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.12em] text-[var(--steel-dim)]">{label}</span>
    <input type="number" min="0" step={step} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className="field mt-2" data-testid={`calc-${id}`} />
  </label>
);

const Out = ({ label, value, big, testid }) => (
  <div className="border-t border-white/12 py-4">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--steel-dim)]">{label}</p>
    <p className={`font-display font-black leading-none ${big ? "text-5xl text-[var(--gold)] sm:text-6xl" : "text-3xl"}`} data-testid={testid}>{value}</p>
  </div>
);

export const Calculator = () => {
  const { t, locale } = useI18n();
  const c = t.calc;
  const [cur, setCur] = useState("EUR");
  const [v, setV] = useState({ price: DEFAULTS.EUR, breads: 1000, days: 26, flour: "", gas: "", staff: "", other: "" });
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const n = (x) => Math.max(0, Number(x) || 0);
  const fmt = (x) => new Intl.NumberFormat(locale, { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(x);

  const daily = n(v.price) * n(v.breads);
  const monthly = daily * n(v.days);
  const charges = ["flour", "gas", "staff", "other"].reduce((s, k) => s + n(v[k]), 0);
  const hasCosts = ["flour", "gas", "staff", "other"].some((k) => v[k] !== "");

  return (
    <section id="rentabilite" className="bg-[var(--navy)] py-24 lg:py-32" data-testid="calculator-section">
      <div className="container-x">
        <SectionHead index="09" eyebrow={c.eyebrow} title={c.title} />
        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          <Reveal className="space-y-5 border border-white/12 bg-[#070C1A]/40 p-7 lg:col-span-6">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.12em] text-[var(--steel-dim)]">{c.currency}</span>
              <select value={cur} onChange={(e) => { setCur(e.target.value); set("price")(DEFAULTS[e.target.value]); }} className="field mt-2" data-testid="calc-currency">
                {CURRENCIES.map((x) => <option key={x} value={x}>{x === "XOF" ? "FCFA (XOF)" : x === "XAF" ? "FCFA (XAF)" : x}</option>)}
              </select>
            </label>
            <div className="grid gap-5 sm:grid-cols-3">
              <Num id="price" label={c.price} value={v.price} onChange={set("price")} />
              <Num id="breads" label={c.breads} value={v.breads} onChange={set("breads")} step="1" />
              <Num id="days" label={c.days} value={v.days} onChange={set("days")} step="1" />
            </div>
            <p className="pt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">{c.optional}</p>
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
              {["flour", "gas", "staff", "other"].map((k) => <Num key={k} id={k} label={c[k]} value={v[k]} onChange={set(k)} />)}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="border border-white/12 p-7 lg:col-span-6">
            <Out label={c.daily} value={fmt(daily)} testid="calc-daily" />
            <Out label={c.monthly} value={fmt(monthly)} testid="calc-monthly" />
            <Out label={c.yearly} value={fmt(monthly * 12)} big testid="calc-yearly" />
            {hasCosts && (
              <div className="mt-2 bg-white/[0.04] p-5" data-testid="calc-simulation">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">{c.sim}</p>
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-[var(--steel-dim)]">{c.charges}</p><p className="font-semibold" data-testid="calc-charges">{fmt(charges)}</p></div>
                  <div><p className="text-[var(--steel-dim)]">{c.resultM}</p><p className="font-semibold" data-testid="calc-result-month">{fmt(monthly - charges)}</p></div>
                  <div><p className="text-[var(--steel-dim)]">{c.resultY}</p><p className="font-semibold" data-testid="calc-result-year">{fmt((monthly - charges) * 12)}</p></div>
                </div>
              </div>
            )}
            <p className="mt-5 text-xs text-[var(--steel-dim)]" data-testid="calc-disclaimer">{c.disclaimer}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
